import React, { useState, useRef, useEffect } from 'react'
import api from '../api/api'; 
import Spinner from '../Spinner';
import ChatGraph from '../ChatGraph';
import ProcessBar from '../ProcessBar';
import {marked} from 'marked';
import Evidence from '../Evidence';
import NotebookIcon from '../NotebookIcon';
import ChatInput from './ChatInput';

const ChatArea = ({ chatId, latestChatHistoryId, updateLatestChatHistoryId, companies, onSaveClick }) => {
    const [selectedCompanies, setSelectedCompanies] = useState(null);
    const [showSpinner, setShowSpinner] = useState(false);
    const [showAgent, setShowAgent] = useState([]);
    const [response, setResponse] = useState('');
    const [messageHistory, setMessageHistory] = useState([]);
    const [showUtteranceArea, setShowUtteranceArea] = useState(false);
    const [nextQuestion, setNextQuestion] = useState('');
    const [nextQuestions, setNextQuestions] = useState([]);
    const [selectedMessage, setSelectedMessage] = useState('');
    const ws = useRef(null);
    const lastChatAreaRef = useRef(null);


    // 選択されている企業を設定
    useEffect(() => {
        setSelectedCompanies(companies);
    }, [companies]);

    // チャットエリアを表示
    const addChatArea = (message) => {
        const newEntry = {
          chatHistoryId: latestChatHistoryId + 1,
          userMessage: message,
          assistantMessage: '',
          graphData: [],
          evidences: [],
        };
        setMessageHistory((prevHistory) => [...prevHistory, newEntry]);
        setShowUtteranceArea(true);
    };

    const handleSendMessage = (e, message) => {
        e.preventDefault();
        // メッセージ送信処理
        if (message.trim()) {
            addChatArea(message);
            setResponse('');
            sendMessage(chatId, message.trim(), true, false);
        }
    };
    
    useEffect(() => {
        // WebSocketの接続を確立
        const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
        ws.current = new WebSocket(`${protocol}://${process.env.REACT_APP_WS_URL}/ws/chat/${chatId}/`);
    
        // WebSocketメッセージ受信時の処理
        ws.current.onmessage = (event) => {
            const data = JSON.parse(event.data);
            if (data.operation_type === 'utterance') {
                // アシスタントの応答をmessageHistoryに追加
                setMessageHistory((prevHistory) => {
                    const updatedHistory = [...prevHistory];
                    const lastEntry = updatedHistory[updatedHistory.length - 1];
                    if (lastEntry) {
                        lastEntry.assistantMessage = data.operation;
                    }
                    return updatedHistory;
                });
                setResponse(data.operation);
            } else if (data.operation_type === 'dashboard') {
                setResponse(data.operation);  // 受信したメッセージを更新
            } else if (data.operation_type === 'agent_operation') {
                if (data.operation === "add_agent") {
                    setShowAgent(prevAgents => [...prevAgents, data.agent_name]);
                } else if (data.operation === 'remove_agent') {
                    setShowAgent(prevAgents => [...prevAgents.slice(0, -1)]);
                }
            } else if (data.operation_type === 'graph_utterance') {
                const figData = Object.entries(data.fig_data).map(([key, fig]) => ({ fig }));

                // グラフデータをmessageHistoryに追加
                setMessageHistory((prevHistory) => {
                    const updatedHistory = [...prevHistory];
                    const lastEntry = updatedHistory[updatedHistory.length - 1];
                    if (lastEntry) {
                        lastEntry.graphData = figData;
                    }
                    return updatedHistory;
                });
            } else if (data.operation_type === "next-questions") {
                setNextQuestion(prevQuestion => {
                    const newQuestion = data.operation;
                
                    // すぐに表示するための処理
                    setNextQuestions(prevQuestions => {
                        const updatedQuestions = [...prevQuestions];
                
                        // 次の質問を最後の要素に表示させつつリアルタイムに反映
                        if (updatedQuestions.length > 0) {
                            updatedQuestions[updatedQuestions.length - 1] = newQuestion.replace('- ', '');
                        } else {
                            // 配列が空の場合、初回の追加処理
                            updatedQuestions.push(newQuestion);
                        }
                
                        return updatedQuestions;
                    });
                
                    // 条件を満たした場合（例えば "~\n" が来た場合）
                    if (data.operation.includes("~\n")) {
                        setNextQuestions(prevQuestions => {
                            const updatedQuestions = [...prevQuestions];
                            
                            // 次の質問が終わったので、新しい空の要素を追加する準備
                            updatedQuestions[updatedQuestions.length - 1] = newQuestion.replace("~\n", "").trim();
                            updatedQuestions.push(""); // 次の新しい質問を受け取るために空の要素を追加
                            
                            return updatedQuestions;
                        });
                
                        setNextQuestion(''); // 現在の `nextQuestion` をリセット
                    }
                  return newQuestion;
              });
            } else {
            }
        };
    
        // WebSocket接続終了時の処理
        return () => {
          if (ws.current) {
            ws.current.close();
          }
        };
    }, [chatId]);

    const sendMessage = async (chatId, prompt) => {
        setShowSpinner(true);
        updateLatestChatHistoryId();
        setNextQuestions([]);
        const secCodes = selectedCompanies.map((company) => company.secCode);
        const companyNames = selectedCompanies.map((company) => company.companyName);
        try {
            const data = {
                task: 'action',
                prompt: prompt,
                is_dashboard: false,
                is_main_chat: true,
                sec_code: secCodes,
                company_name: companyNames,
            }
            const response = await api.post(`${process.env.REACT_APP_API_URL}/chat/api/send-chat/${chatId}/`, data);
            // グラフデータをmessageHistoryに追加
            setMessageHistory((prevHistory) => {
              const updatedHistory = [...prevHistory];
              const lastEntry = updatedHistory[updatedHistory.length - 1];
              if (lastEntry) {
                  lastEntry.evidences = response.data.pdf_path_list;
              }
              return updatedHistory;
          });
        } catch (error) {
            console.error('Error saving note:', error);
        }
        setShowSpinner(false);
      }
    
    useEffect(() => {
        if (lastChatAreaRef.current) {
            setTimeout(() => {
                try {
                    lastChatAreaRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } catch (error) {
                    console.error('Scroll error:', error);
                }
            }, 100);
        }
    }, [messageHistory]);

    const ChatContainer = ({chatHistoryId, userMessage, assistantMessage, graphData, isLast, response, evidences, nextQuestions}) => {
      return (
        <div 
          className={`main-chat-response-display ${isLast ? "main-chat-expand" : ""}`}
          ref={isLast ? lastChatAreaRef : null}
          >
          <div className='main-chat-user-message-display' data-chat-history-id={chatHistoryId}>
              <div className="user-messge">{userMessage}</div>
              <NotebookIcon chatHistoryId={chatHistoryId + 1} onClick={onSaveClick} />
          </div>
          {(isLast && showSpinner) && 
            <div className='dashboard-process-bar'>
                {(isLast && showAgent.length > 0) && <ProcessBar agents={showAgent} /> }
                <Spinner />
            </div>
            }
          
          <div className='main-chat-assistant-response-display' data-chat-history-id={chatHistoryId + 1}>
              {isLast ? <p dangerouslySetInnerHTML={{ __html: marked(response) }}></p> : <p dangerouslySetInnerHTML={{ __html: marked(assistantMessage) }}></p>}
              {graphData.length > 0 && <ChatGraph graphs={graphData} />}
              {evidences.length > 0 && <Evidence evidences={evidences} />}
          </div>
          {nextQuestions.length > 0 ? 
              <>
                  <div className="next-question-examples">Next Question ?</div>
                  <div className="first-introduction">
                      {nextQuestions.map((nextQuestion, index) => (
                          <div key={index} className="introduction-content" onClick={() => setSelectedMessage(nextQuestion)}>{nextQuestion}</div>
                      ))
                        }
                  </div>
              </>
            : null}
        </div>
      )
    }

    return (
        <>
            <div className="main-chat-container">
                <ChatInput handleSendMessage={handleSendMessage} enable={selectedCompanies?.length <= 5 && !showSpinner} selectedMessage={selectedMessage}/>
            </div>
            <div className="main-chat-output-area">
                {(showUtteranceArea && selectedCompanies?.length <= 5) ? 
                    messageHistory.map((messageData, index) => (
                        <ChatContainer 
                            key={messageData.chatHistoryId}
                            chatHistoryId={messageData.chatHistoryId}
                            userMessage={messageData.userMessage} 
                            assistantMessage={messageData.assistantMessage} 
                            graphData={messageData.graphData} 
                            isLast={messageHistory.length === index + 1} 
                            response={messageHistory.length === index + 1 ? response : ''}
                            evidences={messageData.evidences}
                            nextQuestions={index + 1 === messageHistory.length && nextQuestions}
                            />
                    ))
                    : 
                    <div id="graph-message">
                        {selectedCompanies?.length > 5 ? 
                          "6社以上の企業については同時に質問できません。企業数を減らしてください。"
                        :
                          "選択した企業について聞きたいことを質問してください。"
                        }
                    </div>
                }
            </div>
        </>
    )
}

export default ChatArea